import { createFileRoute } from "@tanstack/react-router";
import {
  DM_Lattice_45deg_1x1,
  DM_Masonry_BrickKing,
  DM_Roof_AsphaltBelmont,
  DM_Wall_BoardLap07,
  DM_Wall_ScaleShingle07,
  DM_Wall_ShakeShingle07,
} from "common/materials/library/designMaterialPath";
import { TemplateIdToName } from "common/materials/library/templateIdToName";
import type { MaterialSrc } from "common/materials/materialSrc";
import { materialCacheKey } from "common/materials/materialSrc";
import degreeToRadian from "common/utils/degreeToRadian";
import { aabb2Copy, aabb2Move, aabb2Scale, aabb2New, aabb2Width } from "geom/aabb2";
import { fillHatchLines2D, fillHatchLinesOnCanvas } from "geom/hatch";
import { HatchMaterialId } from "geom/hatchMaterialId";
import { HatchMaterialType, type HatchMaterial } from "geom/hatchTypes";
import { polyLine2Explode } from "geom/polyLine2";
import { hatchMaterialFromDesignMaterial } from "gfx/hatchMaterialFromDesignMaterial";
import { HTTPMaterialParameterLoader } from "gfx/HTTPMaterialParameterLoader";
import { preprocessMaterialParams } from "gfx/materials/designMaterialParams";
import { defaultHatchMaterialFactory } from "lib/house/plans/hatchMaterialFactory";
import getPageTitle from "lib/utils/getPageTitle";
import { useEffect, useRef, useState } from "react";
import { Helmet as Head } from "react-helmet-async";
import withAuthentication from "../../components/withAuthentication";

const testMaterials = [
  DM_Wall_BoardLap07.C,
  DM_Masonry_BrickKing.C,
  DM_Wall_ScaleShingle07.C,
  DM_Wall_ShakeShingle07.C,
  DM_Roof_AsphaltBelmont.Default,
  DM_Lattice_45deg_1x1.C,
];

async function runGenerateBenchmark() {
  const materialParamsSet = await Promise.all(
    testMaterials.map(async (material) => {
      return await HTTPMaterialParameterLoader.instance.fetchDesignMaterialParams(material);
    }),
  );

  const startTime = performance.now();
  const iterationCount = 30;
  for (let i = 0; i < iterationCount; ++i) {
    for (const materialParams of materialParamsSet) {
      if (!materialParams) {
        continue;
      }
      hatchMaterialFromDesignMaterial(preprocessMaterialParams(materialParams));
    }
  }
  const endTime = performance.now();
  return endTime - startTime;
}

function MaterialPreview({
  material,
  forceAutogenerated,
  showVertices,
  showTileBounds,
  highlightOnePatternInstance,
}: {
  material: MaterialSrc;
  forceAutogenerated: boolean;
  showVertices: boolean;
  showTileBounds: boolean;
  highlightOnePatternInstance: boolean;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [timeToGenerate, setTimeToGenerate] = useState(0);
  const [geometryDensity, setGeometryDensity] = useState(0); // Number of lines per unit area
  const [zoomLevel, setZoomLevel] = useState(0);
  const [hatchType, setHatchType] = useState<string | undefined>(undefined);
  const [worldOffset, setWorldOffset] = useState([0, 0]);
  const [verticesInPreviewArea, setVerticesInPreviewArea] = useState(0);
  const [hatchMaterial, setHatchMaterial] = useState<HatchMaterial | undefined>(undefined);

  useEffect(() => {
    void (async () => {
      const materialParams = await HTTPMaterialParameterLoader.instance.fetchDesignMaterialParams(material);
      if (!materialParams) {
        return;
      }
      const loadMaterialId = materialParams.hatchMaterialId;
      if (loadMaterialId === "Auto" || forceAutogenerated) {
        const startTime = performance.now();
        const hatchMaterial = hatchMaterialFromDesignMaterial(preprocessMaterialParams(materialParams));
        const endTime = performance.now();
        setTimeToGenerate(endTime - startTime);
        setHatchType("Autogenerated hatch material");
        setHatchMaterial(hatchMaterial);
        let totalLines = 0;
        for (const line of hatchMaterial.lines) {
          totalLines += line.length / 2 - 1;
        }
        setGeometryDensity(totalLines / (hatchMaterial.sizeU * hatchMaterial.sizeV));
      } else {
        const hatchMaterial = await defaultHatchMaterialFactory.getLibraryHatchMaterial(
          HatchMaterialId[loadMaterialId],
        );
        setHatchType("Library hatch material");
        setHatchMaterial(hatchMaterial);
        if (hatchMaterial.type === HatchMaterialType.PATTERN) {
          setGeometryDensity(-1);
        }
      }
    })();
  }, [material, forceAutogenerated]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    const context = canvas.getContext("2d");
    if (!context) {
      return;
    }
    if (!hatchMaterial) {
      return;
    }
    context.fillStyle = "white";
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.lineWidth = 1;

    const canvasAabb = aabb2New(0, 0, canvas.width, canvas.height);
    const worldWidth = 32 * 2 ** zoomLevel;

    const aabbWidth = aabb2Width(canvasAabb);
    const scale = aabbWidth / worldWidth;
    const hatchAabb2 = aabb2Scale(aabb2Copy(canvasAabb), 1 / scale);
    aabb2Move(hatchAabb2, worldOffset[0], worldOffset[1]);
    const lines = fillHatchLines2D(hatchMaterial, hatchAabb2);
    let countVerticesInPreview = 0;
    for (const line of lines) {
      const exploded = polyLine2Explode(line);
      for (const subLine of exploded) {
        for (let i = 0; i < subLine.length; i += 2) {
          if (
            line[i] >= hatchAabb2.minX &&
            line[i] <= hatchAabb2.maxX &&
            line[i + 1] >= hatchAabb2.minY &&
            line[i + 1] <= hatchAabb2.maxY
          ) {
            ++countVerticesInPreview;
          }
        }
      }
    }
    setVerticesInPreviewArea(countVerticesInPreview);

    const hatchToCanvas = (x: number, y: number): [number, number] => {
      let x1 = x;
      let y1 = y;
      if (hatchMaterial.type === HatchMaterialType.DESIGN) {
        const radians = degreeToRadian(hatchMaterial.rotation);
        const s = Math.sin(radians);
        const c = Math.cos(radians);
        x1 = x * c - y * s;
        y1 = x * s + y * c;
      }
      const x2 = (x1 - worldOffset[0]) * scale;
      const y2 = (y1 - worldOffset[1]) * scale;
      return [x2, canvas.height - y2];
    };

    if (showTileBounds && hatchMaterial.type === HatchMaterialType.DESIGN) {
      const sizeU = hatchMaterial.sizeU;
      const sizeV = hatchMaterial.sizeV;
      context.strokeStyle = "cyan";
      // Draw repeating grid lines at every sizeU and sizeV interval, scaled to canvas coordinates
      const { minX, minY, maxX, maxY } = hatchAabb2;
      // Draw vertical lines (U direction)
      // TODO: The bounding box is not correct for rotated hatch materials.
      for (let x = Math.ceil(minX / sizeU) * sizeU; x <= maxX; x += sizeU) {
        const canvasX1 = hatchToCanvas(x, -1000)[0];
        const canvasY1 = hatchToCanvas(x, -1000)[1];
        const canvasX2 = hatchToCanvas(x, 1000)[0];
        const canvasY2 = hatchToCanvas(x, 1000)[1];
        context.beginPath();
        context.moveTo(canvasX1, canvasY1);
        context.lineTo(canvasX2, canvasY2);
        context.stroke();
      }
      // Draw horizontal lines (V direction)
      for (let y = Math.ceil(minY / sizeV) * sizeV; y <= maxY; y += sizeV) {
        const canvasX1 = hatchToCanvas(-1000, y)[0];
        const canvasY1 = hatchToCanvas(-1000, y)[1];
        const canvasX2 = hatchToCanvas(1000, y)[0];
        const canvasY2 = hatchToCanvas(1000, y)[1];
        context.beginPath();
        context.moveTo(canvasX1, canvasY1);
        context.lineTo(canvasX2, canvasY2);
        context.stroke();
      }
    }

    if (highlightOnePatternInstance && hatchMaterial.type === HatchMaterialType.DESIGN) {
      context.lineWidth = 3;
      context.strokeStyle = "cyan";
      context.beginPath();
      const canvasX1 = hatchToCanvas(0, 0)[0];
      const canvasY1 = hatchToCanvas(0, 0)[1];
      const canvasX2 = hatchToCanvas(hatchMaterial.sizeU, 0)[0];
      const canvasY2 = hatchToCanvas(hatchMaterial.sizeU, 0)[1];
      const canvasX3 = hatchToCanvas(hatchMaterial.sizeU, hatchMaterial.sizeV)[0];
      const canvasY3 = hatchToCanvas(hatchMaterial.sizeU, hatchMaterial.sizeV)[1];
      const canvasX4 = hatchToCanvas(0, hatchMaterial.sizeV)[0];
      const canvasY4 = hatchToCanvas(0, hatchMaterial.sizeV)[1];
      context.moveTo(canvasX1, canvasY1);
      context.lineTo(canvasX2, canvasY2);
      context.lineTo(canvasX3, canvasY3);
      context.lineTo(canvasX4, canvasY4);
      context.closePath();
      context.stroke();

      for (const line of hatchMaterial.lines) {
        context.strokeStyle = "magenta";
        context.beginPath();
        const canvasCoordsStart = hatchToCanvas(line[0], line[1]);
        context.moveTo(canvasCoordsStart[0], canvasCoordsStart[1]);
        for (let i = 2; i < line.length; i += 2) {
          const canvasCoords = hatchToCanvas(line[i], line[i + 1]);
          context.lineTo(canvasCoords[0], canvasCoords[1]);
        }
        context.stroke();
      }
    }

    context.lineWidth = 1;
    context.strokeStyle = "black";
    fillHatchLinesOnCanvas(
      context,
      hatchMaterial,
      canvasAabb,
      worldWidth,
      worldOffset[0],
      worldOffset[1],
      showVertices,
    );
  }, [hatchMaterial, zoomLevel, showVertices, showTileBounds, highlightOnePatternInstance, worldOffset]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    // Mouse drag to adjust worldOffset
    let isDragging = false;
    let lastMousePos: [number, number] | null = null;

    const handleMouseDown = (event: MouseEvent): void => {
      isDragging = true;
      lastMousePos = [event.clientX, event.clientY];
    };

    const handleMouseMove = (event: MouseEvent): void => {
      if (!isDragging || lastMousePos === null) {
        return;
      }
      const dx = event.clientX - lastMousePos[0];
      const dy = event.clientY - lastMousePos[1];
      // Convert pixel delta to world units
      const worldWidth = 32 * 2 ** zoomLevel;
      const scale = worldWidth / canvas.width;
      setWorldOffset(([ox, oy]) => [ox - dx * scale, oy + dy * scale]);
      lastMousePos = [event.clientX, event.clientY];
    };

    const handleMouseUp = (): void => {
      isDragging = false;
      lastMousePos = null;
    };

    canvas.addEventListener("mousedown", handleMouseDown);
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      canvas.removeEventListener("mousedown", handleMouseDown);
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [zoomLevel]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    const handleWheel = (event: WheelEvent): void => {
      event.preventDefault();
      // Calculate zoom delta
      const zoomDelta = event.deltaY < 0 ? -0.25 : 0.25;
      // Get mouse position relative to canvas
      const rect = canvas.getBoundingClientRect();
      const mouseX = event.clientX - rect.left;
      const mouseY = event.clientY - rect.top;
      // Convert to world coordinates before zoom
      const prevWorldWidth = 32 * 2 ** zoomLevel;
      const mouseWorldX = worldOffset[0] + (mouseX / canvas.width) * prevWorldWidth;
      const mouseWorldY = worldOffset[1] + (1 - mouseY / canvas.height) * prevWorldWidth;
      // Update zoom level
      setZoomLevel((prevZoom) => {
        const newZoom = Math.max(-10, Math.min(2, prevZoom + zoomDelta));
        // After zoom, adjust worldOffset so that the point under the mouse stays fixed
        const newWorldWidth = 32 * 2 ** newZoom;
        const newWorldOffsetX = mouseWorldX - (mouseX / canvas.width) * newWorldWidth;
        const newWorldOffsetY = mouseWorldY - (1 - mouseY / canvas.height) * newWorldWidth;
        setWorldOffset([newWorldOffsetX, newWorldOffsetY]);
        return newZoom;
      });
    };

    canvas.addEventListener("wheel", handleWheel, { passive: false });
    return () => {
      canvas.removeEventListener("wheel", handleWheel);
    };
  }, [zoomLevel, worldOffset]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "10px", width: "450px" }}>
      <h2 style={{ marginBottom: 0 }}>{TemplateIdToName[material.templateId]}</h2>
      <div>time to generate: {timeToGenerate?.toFixed(2)}ms</div>
      <div>geometry density: {geometryDensity < 0 ? "N/A" : geometryDensity?.toFixed(3)}</div>
      <div>hatch type: {hatchType}</div>
      <button
        onClick={() => {
          setZoomLevel(0);
          setWorldOffset([0, 0]);
        }}
      >
        Reset View
      </button>
      <div>
        vertices in preview area: {verticesInPreviewArea}
        <br />
        note: vertices where two lines meet are counted twice
      </div>
      <canvas width={450} height={450} ref={canvasRef} />
    </div>
  );
}

function HatchMaterialGenerationTestPage() {
  const [forceAutogenerated, setForceAutogenerated] = useState(true);
  const [showVertices, setShowVertices] = useState(true);
  const [showTileBounds, setShowTileBounds] = useState(false);
  const [highlightOnePatternInstance, sethighlightOnePatternInstance] = useState(false);
  const [generateBenchmarkResult, setGenerateBenchmarkResult] = useState<number | undefined>(undefined);
  return (
    <>
      <Head>
        <title>{getPageTitle("Hatching material generation")}</title>
        <meta name="robots" content="noindex" />
        <style>{`
          body {
            margin: 0;
            padding: 10px;
            background-color: lightgray;
          }
        `}</style>
      </Head>
      <div>
        <div style={{ display: "flex", flexDirection: "row", gap: "20px", justifyContent: "center" }}>
          <label>
            <input
              type="checkbox"
              checked={forceAutogenerated}
              onChange={(e) => setForceAutogenerated(e.target.checked)}
            />
            Force autogenerated
          </label>
          <label>
            <input type="checkbox" checked={showVertices} onChange={(e) => setShowVertices(e.target.checked)} />
            Show vertices
          </label>
          <label>
            <input type="checkbox" checked={showTileBounds} onChange={(e) => setShowTileBounds(e.target.checked)} />
            Show tile bounds
          </label>
          <label>
            <input
              type="checkbox"
              checked={highlightOnePatternInstance}
              onChange={(e) => sethighlightOnePatternInstance(e.target.checked)}
            />
            Highlight one pattern instance
          </label>
          <button onClick={async () => setGenerateBenchmarkResult(await runGenerateBenchmark())}>Run benchmark</button>
          <div>Last benchmark result: {generateBenchmarkResult?.toFixed(2)}ms</div>
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            flexWrap: "wrap",
            gap: "10px",
            justifyContent: "center",
          }}
        >
          {testMaterials.map((material) => (
            <MaterialPreview
              key={materialCacheKey(material)}
              material={material}
              forceAutogenerated={forceAutogenerated}
              showVertices={showVertices}
              showTileBounds={showTileBounds}
              highlightOnePatternInstance={highlightOnePatternInstance}
            />
          ))}
        </div>
      </div>
    </>
  );
}

export const Route = createFileRoute("/debug/hatch-material-generation")({
  component: withAuthentication(HatchMaterialGenerationTestPage, { superAdmin: true }),
});
